'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Eye,
  Globe,
  HelpCircle,
  Layers,
  MapPin,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  Search,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react'
import {
  PageFrame,
  ModuleStack,
  Module,
  ModuleFooter,
  Segmented,
  Tabs,
  StatusPill,
  Tag,
  Toolbar,
  SearchInput,
  EmptyState,
  LoadingState,
  AttentionNote,
  Fields,
  type MetricDef,
  type PillTone,
} from '../ui'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { PrivateItem, PrivateListToggle, usePrivateList } from '../privateColumns'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { apiGet } from './entriesApi'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// ─── Types matching public.get_calls_app_godview_metrics JSONB ───────────────

export type CallsGodviewWindow = '24h' | '7d' | '30d' | '90d' | '365d'
export type CallsGodviewSource =
  | 'all'
  | 'mobilesearch'
  | 'manual'
  | 'bon'
  | 'feedsearch'
  | 'project-mirror'
  | 'multibanco-validator'
export type CallsGodviewAuth = 'all' | 'logged_in' | 'logged_out'

export type CallsGodviewKpis = {
  unique_actors_total: number
  unique_users_logged_in: number
  unique_browsers_logged_out: number
  unique_browsers_all: number
  call_shield_actors: number
  call_shield_users_logged_in: number
  call_shield_browsers_logged_out: number
  call_shield_browsers_all: number
  calls_received_raw: number
  calls_received_dedup: number
  calls_received_logged_in: number
  calls_received_logged_out: number
  calls_matched_dedup: number
  calls_unmatched_dedup: number
  calls_hit_rate_pct: number
  calls_auto_block_eligible: number
  calls_auto_block_rate_pct: number
  calls_distinct_numbers: number
  cross_user_caller_numbers?: number
  cross_user_calls_dedup?: number
  cross_user_max_users_called?: number
  call_submissions_total: number
  call_modal_rejects: number
  call_classifications: number
  call_submitters_logged_in: number
  call_submitters_logged_out: number
  manual_searches_total: number
  manual_searches_logged_in: number
  manual_searches_logged_out: number
  manual_searches_matched: number
  manual_search_hit_rate_pct: number
  manual_distinct_queries: number
  blind_spot_multi_actor_count: number
  votes_total: number
  scrapes_total: number
  scrapes_last_24h: number
  scrapes_hours_since_last: number | null
}

export type DailySeriesRow = {
  day: string
  active_actors: number
  logged_in_users: number
  logged_out_browsers: number
  call_shield_actors: number
  calls_received_dedup: number
  calls_received_raw: number
  calls_matched_dedup: number
  calls_unmatched_dedup: number
  manual_searches: number
  manual_matched: number
  manual_unmatched: number
  call_submissions: number
  call_modal_rejects: number
  call_classifications: number
  votes: number
}

export type SurfaceBreakdownRow = {
  source: string
  total_requests: number
  dedup_requests: number
  matched_requests: number
  hit_rate_pct: number
  distinct_queries: number
  logged_in_users: number
  logged_out_browsers: number
  total_actors: number
}

export type StickinessBucketRow = {
  bucket: string
  all_actors: number
  logged_in_users: number
  logged_out_browsers: number
  call_shield_actors: number
  manual_search_actors: number
}

export type CallClassificationRow = {
  call_type_id: number
  call_type_name: string
  total_count: number
  logged_in_count: number
  logged_out_count: number
  distinct_numbers: number
}

export type ImpersonatedBrandRow = {
  platform_name: string
  platform_id: number | null
  user_reports_count: number
  distinct_numbers: number
}

export type MobileDeviceRow = {
  device_model: string
  actors_count: number
  logged_in_count: number
  logged_out_count: number
}

export type GeoCityRow = {
  country: string
  city: string
  actors_count: number
  calls_received_dedup: number
  manual_searches: number
}

export type TopActiveActorRow = {
  actor_id: string
  is_logged_in: boolean
  user_id: string | null
  user_email_masked: string | null
  primary_browser_id: string | null
  linked_browsers_count: number
  device_model: string | null
  city: string | null
  country: string | null
  asn: string | null
  calls_received_dedup: number
  calls_received_raw: number
  calls_matched: number
  call_submissions: number
  manual_searches: number
  votes_cast: number
  active_days: number
  first_seen: string | null
  last_seen: string | null
}

export type TopIncomingNumberRow = {
  clean_num: string
  phone_number: string
  calls_received_dedup: number
  calls_received_raw: number
  unique_actors_called: number
  logged_in_victims: number
  logged_out_victims: number
  matched_calls: number
  unmatched_calls: number
  user_rejects_count: number
  user_reports_count: number
  scrapes_count: number
  scrapes_31d_block_count: number
  auto_block_eligible: boolean
  call_type_name: string | null
  platform_name: string | null
  protection_status: 'auto_blocked' | 'warned' | 'unmatched_reported' | 'blind_spot'
  first_ring: string | null
  last_ring: string | null
}

export type BlindSpotNumberRow = {
  clean_num: string
  phone_number: string
  unique_actors: number
  logged_in_actors: number
  logged_out_actors: number
  mobilesearch_calls: number
  manual_searches: number
  total_lookups: number
  sources: string[]
  first_seen: string | null
  last_seen: string | null
  later_user_reports: number
  later_call_type_name: string | null
}

export type CallsGodviewPayload = {
  window: {
    since: string
    until: string
    source: string
    auth: string
    dedup_seconds: number
  }
  kpis: CallsGodviewKpis
  daily_series: DailySeriesRow[]
  surface_breakdown: SurfaceBreakdownRow[]
  stickiness_buckets: StickinessBucketRow[]
  call_classifications_breakdown: CallClassificationRow[]
  top_impersonated_brands: ImpersonatedBrandRow[]
  mobile_devices: MobileDeviceRow[]
  geo_cities: GeoCityRow[]
  top_active_actors: TopActiveActorRow[]
  top_incoming_numbers: TopIncomingNumberRow[]
  blind_spot_numbers: BlindSpotNumberRow[]
  _meta?: {
    cached: boolean
    cached_at: string
    window_preset: CallsGodviewWindow
  }
}

// ─── Formatting Helpers ──────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, { label: string; desc: string; tone: PillTone }> = {
  mobilesearch: {
    label: 'App call screening',
    desc: 'Automatic lookup by the installed mobile app when the phone rings',
    tone: 'signal',
  },
  bon: {
    label: 'Web search · BurlaOuNão',
    desc: 'Typed search bar lookup on the BurlaOuNão website or app',
    tone: 'success',
  },
  feedsearch: {
    label: 'Web search · Community feed',
    desc: 'Typed search inside the public web community feed',
    tone: 'neutral',
  },
  'project-mirror': {
    label: 'Web search · Brand portals',
    desc: 'Typed search on public brand trust-center websites',
    tone: 'warning',
  },
  'multibanco-validator': {
    label: 'Web search · Multibanco validator',
    desc: 'Web entity and reference validation checks',
    tone: 'neutral',
  },
}

const PROTECTION_META: Record<
  TopIncomingNumberRow['protection_status'],
  { label: string; tone: PillTone; title: string }
> = {
  auto_blocked: {
    label: 'Auto-blocked (3+ in 31d)',
    tone: 'danger',
    title: 'Meets the 31-day auto-block rule (3+ reports or dataset entries on a blocking call classification)',
  },
  warned: {
    label: 'Flagged',
    tone: 'warning',
    title: 'Matched in the call dataset and warning shown on the consumer’s phone',
  },
  unmatched_reported: {
    label: 'Reported by user',
    tone: 'signal',
    title: 'Unmatched on first ring, then reported or rejected by the consumer',
  },
  blind_spot: {
    label: 'Unmatched',
    tone: 'neutral',
    title: 'No match in the call dataset when the phone rang',
  },
}

function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0'
  return n.toLocaleString('en-US')
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0%'
  return `${Number(n).toFixed(1)}%`
}

function fmtRelTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ms = Date.parse(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`)
  if (!Number.isFinite(ms)) return '—'
  const diffMin = Math.floor((Date.now() - ms) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 48) return `${diffH}h ago`
  const diffD = Math.floor(diffH / 24)
  return `${diffD}d ago`
}

function fmtShortDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`)
  if (!Number.isFinite(d.getTime())) return '—'
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

// ─── Daily Telemetry Chart ───────────────────────────────────────────────────

type ChartMode = 'calls' | 'identities' | 'searches'

function DailyTelemetryChart({
  series,
  mode,
}: {
  series: DailySeriesRow[]
  mode: ChartMode
}) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  if (!series || series.length === 0) {
    return <EmptyState title="No daily activity in this window" className="py-12" />
  }

  const maxVal = Math.max(
    1,
    ...series.map((d) => {
      if (mode === 'calls') return Math.max(d.calls_received_dedup, d.call_submissions)
      if (mode === 'identities') return Math.max(d.logged_in_users + d.logged_out_browsers, d.active_actors)
      return Math.max(d.manual_searches, d.votes)
    }),
  )

  const hovered = hoverIdx != null && series[hoverIdx] ? series[hoverIdx] : series[series.length - 1]

  return (
    <div className="px-5 py-4">
      {/* Legend + Hovered Day Summary */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-3 font-mono text-micro">
        <div className="flex flex-wrap items-center gap-4">
          {mode === 'calls' && (
            <>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-[1px] bg-success" />
                Matched calls
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-[1px] bg-warning" />
                Unmatched calls
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-full bg-destructive" />
                Call reports
              </span>
            </>
          )}
          {mode === 'identities' && (
            <>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-[1px] bg-signal" />
                Logged-in users
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-[1px] bg-muted-foreground/50" />
                Logged-out browsers (web &amp; app)
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-full bg-success" />
                App phones screening calls
              </span>
            </>
          )}
          {mode === 'searches' && (
            <>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-[1px] bg-signal" />
                Matched web searches
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-[1px] bg-muted-foreground/40" />
                Unmatched web searches
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span className="h-2.5 w-2.5 rounded-full bg-success" />
                Web votes
              </span>
            </>
          )}
        </div>

        {hovered && (
          <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
            <span className="font-semibold text-foreground">{hovered.day}</span>
            {mode === 'calls' && (
              <>
                <span>
                  Calls: <strong className="text-foreground">{fmtNum(hovered.calls_received_dedup)}</strong> ({fmtNum(hovered.calls_received_raw)} rings)
                </span>
                <span className="text-success">Matched: {fmtNum(hovered.calls_matched_dedup)}</span>
                <span className="text-warning">Unmatched: {fmtNum(hovered.calls_unmatched_dedup)}</span>
                <span className="text-destructive">
                  Reports: {fmtNum(hovered.call_submissions)} ({fmtNum(hovered.call_modal_rejects)} rejected on ring)
                </span>
              </>
            )}
            {mode === 'identities' && (
              <>
                <span>
                  Visitors: <strong className="text-foreground">{fmtNum(hovered.active_actors)}</strong>
                </span>
                <span className="text-signal">Logged in: {fmtNum(hovered.logged_in_users)}</span>
                <span>Logged-out browsers: {fmtNum(hovered.logged_out_browsers)}</span>
                <span className="text-success">App phones screening calls: {fmtNum(hovered.call_shield_actors)}</span>
              </>
            )}
            {mode === 'searches' && (
              <>
                <span>
                  Web searches: <strong className="text-foreground">{fmtNum(hovered.manual_searches)}</strong>
                </span>
                <span className="text-signal">Matched: {fmtNum(hovered.manual_matched)}</span>
                <span>Unmatched: {fmtNum(hovered.manual_unmatched)}</span>
                <span className="text-success">Votes: {fmtNum(hovered.votes)}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Stacked Bar Strip */}
      <div className="relative flex h-44 items-end gap-[2px] pt-4">
        {series.map((d, idx) => {
          let primary = 0
          let secondary = 0
          let marker = 0
          let primaryCls = 'bg-signal'
          let secondaryCls = 'bg-muted-foreground/35'
          let markerCls = 'bg-destructive'

          if (mode === 'calls') {
            primary = d.calls_matched_dedup
            secondary = d.calls_unmatched_dedup
            marker = d.call_submissions
            primaryCls = 'bg-success'
            secondaryCls = 'bg-warning/80'
            markerCls = 'bg-destructive'
          } else if (mode === 'identities') {
            primary = d.logged_in_users
            secondary = d.logged_out_browsers
            marker = d.call_shield_actors
            primaryCls = 'bg-signal'
            secondaryCls = 'bg-muted-foreground/40'
            markerCls = 'bg-success'
          } else {
            primary = d.manual_matched
            secondary = d.manual_unmatched
            marker = d.votes
            primaryCls = 'bg-signal'
            secondaryCls = 'bg-muted-foreground/35'
            markerCls = 'bg-success'
          }

          const total = primary + secondary
          const totalPct = Math.min(100, (total / maxVal) * 100)
          const primaryPct = total > 0 ? (primary / total) * totalPct : 0
          const secondaryPct = total > 0 ? (secondary / total) * totalPct : 0
          const markerPct = Math.min(100, (marker / maxVal) * 100)
          const isHovered = hoverIdx === idx

          return (
            <div
              key={d.day}
              onMouseEnter={() => setHoverIdx(idx)}
              onMouseLeave={() => setHoverIdx(null)}
              className={`group relative flex h-full flex-1 flex-col justify-end rounded-[1px] transition-colors ${
                isHovered ? 'bg-secondary/80' : 'hover:bg-secondary/40'
              }`}
            >
              {secondaryPct > 0 && (
                <div
                  style={{ height: `${Math.max(secondaryPct, 1.5)}%` }}
                  className={`w-full rounded-t-[1px] ${secondaryCls}`}
                />
              )}
              {primaryPct > 0 && (
                <div
                  style={{ height: `${Math.max(primaryPct, 1.5)}%` }}
                  className={`w-full ${secondaryPct === 0 ? 'rounded-t-[1px]' : ''} ${primaryCls}`}
                />
              )}
              {marker > 0 && (
                <span
                  style={{ bottom: `calc(${markerPct}% - 3px)` }}
                  className={`pointer-events-none absolute left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full ring-1 ring-background ${markerCls}`}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* X-Axis Labels */}
      <div className="mt-2 flex justify-between font-mono text-micro text-subtle">
        <span>{series[0]?.day}</span>
        {series.length > 4 && <span>{series[Math.floor(series.length / 2)]?.day}</span>}
        <span>{series[series.length - 1]?.day}</span>
      </div>
    </div>
  )
}

// ─── Main View Component ─────────────────────────────────────────────────────

export function CallsGodviewView({ theme = 'light' }: { theme?: ProcessedTheme }) {
  const [windowPreset, setWindowPreset] = useState<CallsGodviewWindow>('30d')
  const [authFilter, setAuthFilter] = useState<CallsGodviewAuth>('all')
  const [sourceFilter, setSourceFilter] = useState<CallsGodviewSource>('all')
  const [dedupSeconds, setDedupSeconds] = useState<number>(60)
  const [chartMode, setChartMode] = useState<ChartMode>('calls')
  const [directoryTab, setDirectoryTab] = useState<'incoming' | 'actors' | 'blindspots'>('incoming')

  // Directory filters
  const [incomingSearch, setIncomingSearch] = useState('')
  const [protectionFilter, setProtectionFilter] = useState<string>('all')
  const [actorSearch, setActorSearch] = useState('')
  const [blindSpotSearch, setBlindSpotSearch] = useState('')

  // Drill-down dialogs
  const [selectedNumber, setSelectedNumber] = useState<TopIncomingNumberRow | null>(null)
  const [selectedActor, setSelectedActor] = useState<TopActiveActorRow | null>(null)

  // Demo-mode privacy + local unhide for non-table lists (surfaces/sources, brands, dialogs)
  const surfacePrivacy = usePrivateList()
  const brandPrivacy = usePrivateList()
  const dialogPrivacy = usePrivateList()

  // Data state
  const [data, setData] = useState<CallsGodviewPayload | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [refreshing, setRefreshing] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)

  const load = useCallback(
    async (forceRefresh = false) => {
      if (forceRefresh || data !== null) setRefreshing(true)
      else setLoading(true)
      setError(null)

      const qs = new URLSearchParams({
        window: windowPreset,
        source: sourceFilter,
        auth: authFilter,
        dedup_seconds: String(dedupSeconds),
      })
      if (forceRefresh) qs.set('refresh', '1')

      const res = await apiGet<CallsGodviewPayload>(`/calls-godview?${qs.toString()}`)
      if (res.success && res.data) {
        setData(res.data)
        setLastUpdated(Date.now())
      } else {
        setError(res.error ?? 'Failed to load Calls & App Godview metrics')
      }
      setLoading(false)
      setRefreshing(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowPreset, sourceFilter, authFilter, dedupSeconds],
  )

  useEffect(() => {
    load(false)
  }, [load])

  const kpis = data?.kpis

  // Top headline metrics strip (5 concise metrics, matching JobsView / PageFrame layout)
  const headlineMetrics = useMemo<MetricDef[]>(() => {
    if (!kpis) return []
    const callsCount = dedupSeconds > 0 ? kpis.calls_received_dedup : kpis.calls_received_raw
    return [
      {
        label: 'Calls screened',
        value: fmtNum(callsCount),
        tone: 'signal',
        note: `${fmtNum(kpis.calls_distinct_numbers)} numbers`,
        title: 'Incoming calls screened on consumer phones',
      },
      {
        label: 'Calling 2+ users',
        value: fmtNum(kpis.cross_user_caller_numbers ?? 0),
        tone: (kpis.cross_user_caller_numbers ?? 0) > 0 ? 'warning' : 'default',
        note: `${fmtNum(kpis.cross_user_calls_dedup ?? 0)} calls`,
        title: 'Phone numbers that rang 2 or more distinct users’ phones',
      },
      {
        label: 'Calls matched',
        value: fmtPct(kpis.calls_hit_rate_pct),
        tone: kpis.calls_hit_rate_pct >= 20 ? 'success' : 'warning',
        note: `${fmtNum(kpis.calls_auto_block_eligible)} auto-blocked`,
        title: 'Share of incoming calls that matched a known number in the call dataset',
      },
      {
        label: 'Call reports',
        value: fmtNum(kpis.call_submissions_total),
        tone: kpis.call_submissions_total > 0 ? 'warning' : 'default',
        note: `${fmtNum(kpis.call_modal_rejects)} on ring`,
        title: 'Call reports submitted by consumers: instant ring rejections and classified scam calls',
      },
      {
        label: 'App & web visitors',
        value: fmtNum(kpis.unique_actors_total),
        tone: 'default',
        note: `${fmtNum(kpis.call_shield_actors)} app phones`,
        title: 'Unique logged-in users and logged-out browsers across app call screening, call reports, and web searches',
      },
    ]
  }, [kpis, dedupSeconds])

  // Filtered directories
  const filteredIncomingNumbers = useMemo(() => {
    const list = data?.top_incoming_numbers ?? []
    const q = incomingSearch.trim().toLowerCase()
    const filtered = list.filter((r) => {
      if (protectionFilter === 'cross_user') {
        if (r.unique_actors_called < 2) return false
      } else if (protectionFilter !== 'all' && r.protection_status !== protectionFilter) {
        return false
      }
      if (!q) return true
      return (
        r.phone_number.toLowerCase().includes(q) ||
        r.clean_num.toLowerCase().includes(q) ||
        (r.call_type_name ?? '').toLowerCase().includes(q) ||
        (r.platform_name ?? '').toLowerCase().includes(q)
      )
    })
    if (protectionFilter === 'cross_user') {
      return [...filtered].sort(
        (a, b) => b.unique_actors_called - a.unique_actors_called || b.calls_received_dedup - a.calls_received_dedup,
      )
    }
    return filtered
  }, [data?.top_incoming_numbers, incomingSearch, protectionFilter])

  const filteredActors = useMemo(() => {
    const list = data?.top_active_actors ?? []
    const q = actorSearch.trim().toLowerCase()
    if (!q) return list
    return list.filter(
      (a) =>
        a.actor_id.toLowerCase().includes(q) ||
        (a.user_email_masked ?? '').toLowerCase().includes(q) ||
        (a.primary_browser_id ?? '').toLowerCase().includes(q) ||
        (a.device_model ?? '').toLowerCase().includes(q) ||
        (a.city ?? '').toLowerCase().includes(q) ||
        (a.country ?? '').toLowerCase().includes(q),
    )
  }, [data?.top_active_actors, actorSearch])

  const filteredBlindSpots = useMemo(() => {
    const list = data?.blind_spot_numbers ?? []
    const q = blindSpotSearch.trim().toLowerCase()
    if (!q) return list
    return list.filter(
      (b) =>
        b.phone_number.toLowerCase().includes(q) ||
        b.clean_num.toLowerCase().includes(q) ||
        (b.later_call_type_name ?? '').toLowerCase().includes(q) ||
        (b.sources ?? []).some((s) => s.toLowerCase().includes(q)),
    )
  }, [data?.blind_spot_numbers, blindSpotSearch])

  // Table Columns
  const incomingColumns = useMemo<ColumnDef[]>(
    () => [
      { key: 'phone', label: 'Phone number', initialWidth: 155, stickyLeft: true },
      { key: 'status', label: 'Status', initialWidth: 175 },
      { key: 'calls', label: 'Calls (dedup / raw)', initialWidth: 145 },
      { key: 'victims', label: 'Users called (in / out)', initialWidth: 175 },
      { key: 'match', label: 'Matched / unmatched', initialWidth: 150 },
      { key: 'reports', label: 'Call reports', initialWidth: 155 },
      { key: 'scrapes', label: 'Dataset matches (31d / all)', initialWidth: 175 },
      { key: 'call_type', label: 'Classification', initialWidth: 150 },
      { key: 'brand', label: 'Brand', initialWidth: 140 },
      { key: 'last_ring', label: 'Last call', initialWidth: 130 },
    ],
    [],
  )

  const actorColumns = useMemo<ColumnDef[]>(
    () => [
      { key: 'identity', label: 'User / browser', initialWidth: 210, stickyLeft: true, private: true },
      { key: 'type', label: 'Account', initialWidth: 155 },
      { key: 'browsers', label: 'Browsers', initialWidth: 90 },
      { key: 'device', label: 'Phone model', initialWidth: 150 },
      { key: 'geo', label: 'Location', initialWidth: 170 },
      { key: 'calls', label: 'App calls screened', initialWidth: 165 },
      { key: 'submissions', label: 'Call reports', initialWidth: 110 },
      { key: 'searches', label: 'Web searches', initialWidth: 130 },
      { key: 'votes', label: 'Votes', initialWidth: 80 },
      { key: 'days', label: 'Active days', initialWidth: 100 },
      { key: 'last_seen', label: 'Last seen', initialWidth: 125 },
    ],
    [],
  )

  const blindSpotColumns = useMemo<ColumnDef[]>(
    () => [
      { key: 'phone', label: 'Phone number', initialWidth: 175, stickyLeft: true },
      { key: 'actors', label: 'Users / browsers (in / out)', initialWidth: 185 },
      { key: 'calls', label: 'App calls screened', initialWidth: 145 },
      { key: 'manual', label: 'Web searches', initialWidth: 130 },
      { key: 'total', label: 'Missed lookups', initialWidth: 145 },
      { key: 'sources', label: 'Channels', initialWidth: 190 },
      { key: 'later', label: 'Later reports', initialWidth: 185 },
      { key: 'first_seen', label: 'First seen', initialWidth: 130 },
      { key: 'last_seen', label: 'Last seen', initialWidth: 130 },
    ],
    [],
  )

  const infoContent = (
    <div className="space-y-1.5 text-[12px]">
      <p>
        Computed on read from incoming call lookups, manual number searches, consumer call reports, and the call
        dataset (<code className="font-mono text-micro">get_calls_app_godview_metrics</code>). Repeated ring lookups
        from the same phone and caller within 60 seconds count as one call.
      </p>
      <p>
        <b>Numbers seen in calls</b> counts distinct phone numbers screened on consumer phones;{' '}
        <b>numbers calling 2+ users</b> stitches logged-in accounts and logged-out background ring lookups on the same
        phone before counting distinct users called.
      </p>
      <p>
        A number is <b>auto-blocked</b> when it has 3 or more reports or dataset entries in the last 31 days on a
        blocking call classification, <b>flagged</b> when it matches an existing entry in the call dataset, and{' '}
        <b>unmatched</b> when no entry existed when the phone rang.
      </p>
    </div>
  )

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented<CallsGodviewWindow>
        size="sm"
        value={windowPreset}
        onChange={setWindowPreset}
        segments={[
          { value: '24h', label: '24h' },
          { value: '7d', label: '7d' },
          { value: '30d', label: '30d' },
          { value: '90d', label: '90d' },
          { value: '365d', label: '365d' },
        ]}
      />
      <Segmented<CallsGodviewAuth>
        size="sm"
        value={authFilter}
        onChange={setAuthFilter}
        segments={[
          { value: 'all', label: 'All users' },
          { value: 'logged_in', label: 'Logged in' },
          { value: 'logged_out', label: 'Logged out' },
        ]}
      />
      <select
        value={sourceFilter}
        onChange={(e) => setSourceFilter(e.target.value as CallsGodviewSource)}
        className="h-7 rounded-[2px] border border-border bg-card px-2.5 font-mono text-micro uppercase tracking-widest text-foreground focus:border-signal focus:outline-none"
        title="Filter channel"
      >
        <option value="all">All channels (app + web)</option>
        <option value="mobilesearch">App call screening</option>
        <option value="manual">All web searches</option>
        <option value="bon">Web search · BurlaOuNão</option>
        <option value="feedsearch">Web search · Community feed</option>
        <option value="project-mirror">Web search · Brand portals</option>
        <option value="multibanco-validator">Web search · MB validator</option>
      </select>
      <button
        type="button"
        onClick={() => setDedupSeconds((prev) => (prev > 0 ? 0 : 60))}
        className={`h-7 rounded-[2px] border px-2.5 font-mono text-micro uppercase tracking-widest transition-colors ${
          dedupSeconds > 0
            ? 'border-signal/50 bg-signal-surface text-signal'
            : 'border-border bg-card text-muted-foreground hover:text-foreground'
        }`}
        title="Collapse repeated ring lookups from the same phone and caller within 60 seconds into a single call"
      >
        {dedupSeconds > 0 ? '60s ring dedup' : 'Raw rings'}
      </button>
    </div>
  )

  return (
    <PageFrame
      eyebrow="Dashboards · Calls"
      title="Calls"
      subtitle="Incoming calls screened on the mobile app, phone numbers seen in calls, consumer call reports, and web searches across Trust Fence."
      info={infoContent}
      headerActions={headerActions}
      metrics={headlineMetrics}
      refresh={{ onRefresh: () => load(true), refreshing, lastUpdated }}
      loading={loading && !data}
      error={error && !data ? error : undefined}
      onRetry={() => load(true)}
      footer={
        data ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span>
              Window: {fmtShortDateTime(data.window.since)} → {fmtShortDateTime(data.window.until)} · Dedup:{' '}
              {data.window.dedup_seconds}s · Channel: {data.window.source} · Users: {data.window.auth}
            </span>
            <span>
              Call dataset: {fmtNum(data.kpis.scrapes_total)} rows ({fmtNum(data.kpis.scrapes_last_24h)} in 24h · last
              updated{' '}
              {data.kpis.scrapes_hours_since_last != null ? `${data.kpis.scrapes_hours_since_last}h ago` : 'unknown'})
            </span>
          </div>
        ) : undefined
      }
    >
      {data && kpis && (
        <div className="space-y-10">
          {/* 8-Tile Deep Telemetry Hairline Grid */}
          <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {/* Tile 1: Logged-in users */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Logged-in users
                </span>
                <UserCheck className="h-3.5 w-3.5 text-signal" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-signal">
                  {fmtNum(kpis.unique_users_logged_in)}
                </span>
                <span className="font-mono text-micro text-subtle">
                  {kpis.unique_actors_total > 0
                    ? fmtPct((kpis.unique_users_logged_in / kpis.unique_actors_total) * 100)
                    : '0%'}{' '}
                  of visitors
                </span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                {fmtNum(kpis.call_shield_users_logged_in)} app call screening · {fmtNum(kpis.call_submitters_logged_in)}{' '}
                call reports · {fmtNum(kpis.manual_searches_logged_in)} web searches
              </div>
            </div>

            {/* Tile 2: Logged-out browsers */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Logged-out browsers (web &amp; app)
                </span>
                <UserX className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-foreground">
                  {fmtNum(kpis.unique_browsers_logged_out)}
                </span>
                <span className="font-mono text-micro text-subtle">
                  ({fmtNum(kpis.call_shield_browsers_logged_out)} app phones)
                </span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                {fmtNum(kpis.call_shield_browsers_logged_out)} app call screening ·{' '}
                {fmtNum(kpis.call_submitters_logged_out)} call reports ·{' '}
                {fmtNum(kpis.manual_searches_logged_out)} web searches
              </div>
            </div>

            {/* Tile 3: Numbers seen in calls */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Numbers seen in calls
                </span>
                <PhoneIncoming className="h-3.5 w-3.5 text-signal" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-signal">
                  {fmtNum(kpis.calls_distinct_numbers)}
                </span>
                <span className="font-mono text-micro text-subtle">
                  across {fmtNum(kpis.calls_received_dedup)} calls ({fmtNum(kpis.calls_received_raw)} rings)
                </span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                {fmtNum(kpis.calls_received_logged_in)} logged in · {fmtNum(kpis.calls_received_logged_out)} logged out
                ·{' '}
                <button
                  type="button"
                  onClick={() => {
                    setDirectoryTab('incoming')
                    setProtectionFilter('cross_user')
                  }}
                  className="text-warning underline decoration-dotted underline-offset-2 hover:text-signal"
                  title="Filter table to phone numbers that rang 2 or more users' phones"
                >
                  {fmtNum(kpis.cross_user_caller_numbers ?? 0)} calling 2+ users
                </button>
              </div>
            </div>

            {/* Tile 4: Calls matched */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Calls matched
                </span>
                <ShieldCheck className="h-3.5 w-3.5 text-success" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-success">
                  {fmtPct(kpis.calls_hit_rate_pct)}
                </span>
                <span className="font-mono text-micro text-subtle">
                  ({fmtNum(kpis.calls_matched_dedup)} matched · {fmtNum(kpis.calls_unmatched_dedup)} unmatched)
                </span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                <span className="text-destructive">{fmtNum(kpis.calls_auto_block_eligible)}</span> calls met 31-day
                auto-block ({fmtPct(kpis.calls_auto_block_rate_pct)})
              </div>
            </div>

            {/* Tile 5: Call reports */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Call reports
                </span>
                <PhoneOff className="h-3.5 w-3.5 text-warning" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-warning">
                  {fmtNum(kpis.call_submissions_total)}
                </span>
                <span className="font-mono text-micro text-subtle">from consumers</span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                {fmtNum(kpis.call_modal_rejects)} rejected on ring · {fmtNum(kpis.call_classifications)} classified by
                type
              </div>
            </div>

            {/* Tile 6: Web searches */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Web searches
                </span>
                <Search className="h-3.5 w-3.5 text-foreground" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-foreground">
                  {fmtNum(kpis.manual_searches_total)}
                </span>
                <span className="font-mono text-micro text-subtle">
                  ({fmtPct(kpis.manual_search_hit_rate_pct)} matched)
                </span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                {fmtNum(kpis.manual_searches_matched)} matched · {fmtNum(kpis.manual_distinct_queries)} unique queries
                · {fmtNum(kpis.votes_total)} web votes
              </div>
            </div>

            {/* Tile 7: Unmatched numbers */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Unmatched numbers (2+ users)
                </span>
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-destructive">
                  {fmtNum(kpis.blind_spot_multi_actor_count)}
                </span>
                <span className="font-mono text-micro text-subtle">not yet in dataset</span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                Numbers with no match on ring looked up by 2 or more users
              </div>
            </div>

            {/* Tile 8: Call dataset */}
            <div className="bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-micro uppercase tracking-widest text-muted-foreground">
                  Call dataset
                </span>
                <Activity
                  className={`h-3.5 w-3.5 ${
                    (kpis.scrapes_hours_since_last ?? 999) <= 24 ? 'text-success' : 'text-warning'
                  }`}
                />
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-h2 font-normal tabular-nums text-foreground">
                  {fmtNum(kpis.scrapes_total)}
                </span>
                <span className="font-mono text-micro text-subtle">
                  ({fmtNum(kpis.scrapes_last_24h)} in 24h)
                </span>
              </div>
              <div className="mt-2 font-mono text-micro text-muted-foreground">
                Last updated:{' '}
                <span
                  className={
                    (kpis.scrapes_hours_since_last ?? 999) <= 24 ? 'text-success' : 'text-warning'
                  }
                >
                  {kpis.scrapes_hours_since_last != null ? `${kpis.scrapes_hours_since_last}h ago` : 'never'}
                </span>
              </div>
            </div>
          </div>

          <ModuleStack>
            {/* ── MODULE 01: Calls per day ── */}
            <Module
              title="Calls per day"
              icon={BarChart3}
              summary={`${data.daily_series.length} days · ${fmtNum(kpis.calls_received_dedup)} calls · ${fmtNum(
                kpis.unique_actors_total,
              )} users`}
              storageKey="calls-godview-daily"
              actions={
                <Segmented<ChartMode>
                  size="sm"
                  value={chartMode}
                  onChange={setChartMode}
                  segments={[
                    { value: 'calls', label: 'Calls & reports' },
                    { value: 'identities', label: 'Logged in vs. logged out' },
                    { value: 'searches', label: 'Web searches & votes' },
                  ]}
                />
              }
            >
              <DailyTelemetryChart series={data.daily_series} mode={chartMode} />
              <ModuleFooter>
                {fmtNum(kpis.calls_received_dedup)} calls ({fmtNum(kpis.calls_received_raw)} rings) ·{' '}
                {fmtNum(kpis.calls_matched_dedup)} matched ({fmtPct(kpis.calls_hit_rate_pct)}) ·{' '}
                {fmtNum(kpis.call_submissions_total)} call reports ({fmtNum(kpis.call_modal_rejects)} rejected on ring,{' '}
                {fmtNum(kpis.call_classifications)} classified)
              </ModuleFooter>
            </Module>

            {/* ── MODULE 02: Channels & user retention ── */}
            <Module
              title="Channels & user retention"
              icon={Layers}
              summary={`${data.surface_breakdown.length} channels · 5 activity buckets`}
              storageKey="calls-godview-surfaces"
            >
              <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-12 lg:divide-x lg:divide-y-0">
                {/* Left 7 cols: Surface Breakdown */}
                <div className="lg:col-span-7">
                  <div className="flex items-center justify-between gap-2 border-b border-border bg-secondary/30 px-4 py-2.5 font-mono text-micro uppercase tracking-widest text-muted-foreground">
                    <span>Lookups and hit rate by channel</span>
                    <PrivateListToggle privacy={surfacePrivacy} />
                  </div>
                  <div className="divide-y divide-border">
                    {data.surface_breakdown.length === 0 ? (
                      <div className="p-6 font-mono text-label text-muted-foreground">No channel activity in window.</div>
                    ) : (
                      data.surface_breakdown.map((s) => {
                        const meta = SOURCE_LABELS[s.source] ?? {
                          label: s.source,
                          desc: 'Lookup channel',
                          tone: 'neutral' as PillTone,
                        }
                        return (
                          <div key={s.source} className="flex flex-col gap-2 px-4 py-3 hover:bg-secondary/30">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <PrivateItem id={s.source} privacy={surfacePrivacy} className="flex items-center gap-2">
                                <StatusPill tone={meta.tone}>{s.source}</StatusPill>
                                <span className="text-body font-medium text-foreground">{meta.label}</span>
                              </PrivateItem>
                              <div className="flex items-center gap-3 font-mono text-label">
                                <span title="Deduplicated lookups">
                                  <strong className="text-foreground">{fmtNum(s.dedup_requests)}</strong> calls/lookups
                                </span>
                                {s.total_requests !== s.dedup_requests && (
                                  <span className="text-subtle">({fmtNum(s.total_requests)} raw)</span>
                                )}
                                <span className="text-signal">{fmtPct(s.hit_rate_pct)} matched</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-micro text-muted-foreground">
                              <PrivateItem id={s.source} privacy={surfacePrivacy}>
                                <span>{meta.desc}</span>
                              </PrivateItem>
                              <span>
                                {fmtNum(s.distinct_queries)} numbers/queries ·{' '}
                                <span className="text-signal">{fmtNum(s.logged_in_users)} logged in</span> ·{' '}
                                <span>{fmtNum(s.logged_out_browsers)} logged out</span>
                              </span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-[1px] bg-secondary">
                              <div
                                className="h-full bg-signal transition-all"
                                style={{ width: `${Math.min(100, Math.max(1, s.hit_rate_pct))}%` }}
                              />
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>

                {/* Right 5 cols: Stickiness Buckets */}
                <div className="lg:col-span-5">
                  <div className="border-b border-border bg-secondary/30 px-4 py-2.5 font-mono text-micro uppercase tracking-widest text-muted-foreground">
                    Active days in window (app &amp; web visitors)
                  </div>
                  <div className="divide-y divide-border">
                    {data.stickiness_buckets.map((b) => {
                      const totalActors = Math.max(1, kpis.unique_actors_total)
                      const sharePct = (b.all_actors / totalActors) * 100
                      return (
                        <div key={b.bucket} className="px-4 py-3">
                          <div className="flex items-center justify-between font-mono text-label">
                            <span className="font-semibold text-foreground">{b.bucket} active</span>
                            <span className="text-foreground">
                              {fmtNum(b.all_actors)} visitors{' '}
                              <span className="text-subtle">({fmtPct(sharePct)})</span>
                            </span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-micro text-muted-foreground">
                            <span className="text-signal">{fmtNum(b.logged_in_users)} logged in</span>
                            <span>·</span>
                            <span>{fmtNum(b.logged_out_browsers)} logged-out browsers</span>
                            <span>·</span>
                            <span className="text-success">{fmtNum(b.call_shield_actors)} app call screening</span>
                            <span>·</span>
                            <span>{fmtNum(b.manual_search_actors)} web searches</span>
                          </div>
                          <div className="mt-2 flex h-1.5 w-full overflow-hidden rounded-[1px] bg-secondary">
                            <div
                              className="h-full bg-signal"
                              style={{
                                width: `${
                                  b.all_actors > 0
                                    ? Math.min(100, (b.logged_in_users / totalActors) * 100)
                                    : 0
                                }%`,
                              }}
                              title={`Logged in: ${b.logged_in_users}`}
                            />
                            <div
                              className="h-full bg-muted-foreground/50"
                              style={{
                                width: `${
                                  b.all_actors > 0
                                    ? Math.min(100, (b.logged_out_browsers / totalActors) * 100)
                                    : 0
                                }%`,
                              }}
                              title={`Logged out: ${b.logged_out_browsers}`}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </Module>

            {/* ── MODULE 03: Classifications, brands, phones & locations ── */}
            <Module
              title="Classifications, brands, phones & locations"
              icon={Smartphone}
              summary={`${data.call_classifications_breakdown.length} classifications · ${data.top_impersonated_brands.length} brands · ${data.mobile_devices.length} phone models`}
              storageKey="calls-godview-breakdowns"
            >
              <div className="grid grid-cols-1 divide-y divide-border md:grid-cols-2 md:divide-x md:divide-y-0 xl:grid-cols-4">
                {/* Col 1: Call Classifications */}
                <div>
                  <div className="border-b border-border bg-secondary/30 px-4 py-2.5 font-mono text-micro uppercase tracking-widest text-muted-foreground">
                    Calls by classification
                  </div>
                  <div className="max-h-80 divide-y divide-border overflow-y-auto">
                    {data.call_classifications_breakdown.length === 0 ? (
                      <div className="p-4 font-mono text-micro text-muted-foreground">No call reports in window.</div>
                    ) : (
                      data.call_classifications_breakdown.map((c) => (
                        <div key={c.call_type_id} className="px-4 py-2.5 hover:bg-secondary/20">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-small font-medium text-foreground">{c.call_type_name}</span>
                            <span className="font-mono text-label font-semibold tabular-nums text-warning">
                              {fmtNum(c.total_count)}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center justify-between font-mono text-micro text-muted-foreground">
                            <span>across {fmtNum(c.distinct_numbers)} numbers</span>
                            <span>
                              {fmtNum(c.logged_in_count)} in · {fmtNum(c.logged_out_count)} out
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Col 2: Top Impersonated Brands in Calls */}
                <div>
                  <div className="flex items-center justify-between gap-2 border-b border-border bg-secondary/30 px-4 py-2.5 font-mono text-micro uppercase tracking-widest text-muted-foreground">
                    <span>Brands impersonated in calls</span>
                    <PrivateListToggle privacy={brandPrivacy} />
                  </div>
                  <div className="max-h-80 divide-y divide-border overflow-y-auto">
                    {data.top_impersonated_brands.length === 0 ? (
                      <div className="p-4 font-mono text-micro text-muted-foreground">
                        No brand attributions in window.
                      </div>
                    ) : (
                      data.top_impersonated_brands.map((b, i) => (
                        <div key={`${b.platform_name}-${i}`} className="px-4 py-2.5 hover:bg-secondary/20">
                          <div className="flex items-center justify-between gap-2">
                            <PrivateItem id={b.platform_name} privacy={brandPrivacy} className="min-w-0">
                              <span className="truncate text-small font-medium text-foreground">{b.platform_name}</span>
                            </PrivateItem>
                            <span className="font-mono text-label font-semibold tabular-nums text-signal">
                              {fmtNum(b.user_reports_count)}
                            </span>
                          </div>
                          <div className="mt-1 font-mono text-micro text-muted-foreground">
                            across {fmtNum(b.distinct_numbers)} numbers
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Col 3: Mobile Device Models */}
                <div>
                  <div className="border-b border-border bg-secondary/30 px-4 py-2.5 font-mono text-micro uppercase tracking-widest text-muted-foreground">
                    Phone models
                  </div>
                  <div className="max-h-80 divide-y divide-border overflow-y-auto">
                    {data.mobile_devices.length === 0 ? (
                      <div className="p-4 font-mono text-micro text-muted-foreground">No phone models recorded.</div>
                    ) : (
                      data.mobile_devices.map((m) => (
                        <div key={m.device_model} className="px-4 py-2.5 hover:bg-secondary/20">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-mono text-label text-foreground">{m.device_model}</span>
                            <span className="font-mono text-label font-semibold tabular-nums text-foreground">
                              {fmtNum(m.actors_count)}
                            </span>
                          </div>
                          <div className="mt-1 font-mono text-micro text-muted-foreground">
                            {fmtNum(m.logged_in_count)} logged in · {fmtNum(m.logged_out_count)} logged out
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Col 4: Top Geo Cities */}
                <div>
                  <div className="border-b border-border bg-secondary/30 px-4 py-2.5 font-mono text-micro uppercase tracking-widest text-muted-foreground">
                    Cities
                  </div>
                  <div className="max-h-80 divide-y divide-border overflow-y-auto">
                    {data.geo_cities.length === 0 ? (
                      <div className="p-4 font-mono text-micro text-muted-foreground">No location data in window.</div>
                    ) : (
                      data.geo_cities.map((g, i) => (
                        <div key={`${g.country}-${g.city}-${i}`} className="px-4 py-2.5 hover:bg-secondary/20">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-small font-medium text-foreground">
                              {g.city}{' '}
                              <span className="font-mono text-micro text-subtle">({g.country})</span>
                            </span>
                            <span className="font-mono text-label font-semibold tabular-nums text-foreground">
                              {fmtNum(g.actors_count)} users
                            </span>
                          </div>
                          <div className="mt-1 font-mono text-micro text-muted-foreground">
                            {fmtNum(g.calls_received_dedup)} app calls · {fmtNum(g.manual_searches)} web searches
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Module>

            {/* ── MODULE 04: Numbers & users ── */}
            <Module
              title="Numbers & users"
              icon={PhoneCall}
              summary={`${data.top_incoming_numbers.length} numbers seen in calls · ${data.top_active_actors.length} active users · ${data.blind_spot_numbers.length} unmatched numbers`}
              storageKey="calls-godview-directories"
            >
              <div className="px-4 pt-2">
                <Tabs
                  value={directoryTab}
                  onChange={setDirectoryTab}
                  tabs={[
                    {
                      value: 'incoming',
                      label: 'Numbers seen in calls',
                      count: data.top_incoming_numbers.length,
                    },
                    {
                      value: 'actors',
                      label: 'Active users & browsers (app + web)',
                      count: data.top_active_actors.length,
                    },
                    {
                      value: 'blindspots',
                      label: 'Unmatched numbers',
                      count: data.blind_spot_numbers.length,
                    },
                  ]}
                />
              </div>

              {/* TAB 1: Top Incoming Numbers */}
              {directoryTab === 'incoming' && (
                <div>
                  <ResizableTable
                    columns={incomingColumns}
                    storageKey="calls-godview-incoming-table"
                    theme={theme}
                    isEmpty={filteredIncomingNumbers.length === 0}
                    emptyMessage="No incoming calls match the current filters."
                    onRefresh={() => load(true)}
                    refreshing={refreshing}
                    lastUpdated={lastUpdated}
                    toolbar={
                      <Toolbar>
                        <SearchInput
                          value={incomingSearch}
                          onChange={setIncomingSearch}
                          placeholder="Filter phone number, classification, brand…"
                        />
                        <Segmented<string>
                          size="sm"
                          value={protectionFilter}
                          onChange={setProtectionFilter}
                          segments={[
                            { value: 'all', label: 'All' },
                            {
                              value: 'cross_user',
                              label: `2+ users (${fmtNum(kpis?.cross_user_caller_numbers ?? 0)})`,
                            },
                            { value: 'auto_blocked', label: 'Auto-blocked' },
                            { value: 'warned', label: 'Flagged' },
                            { value: 'blind_spot', label: 'Unmatched' },
                          ]}
                        />
                        <span className="font-mono text-micro text-muted-foreground">
                          {filteredIncomingNumbers.length} of {data.top_incoming_numbers.length} numbers
                        </span>
                      </Toolbar>
                    }
                  >
                    {filteredIncomingNumbers.map((row) => {
                      const pMeta = PROTECTION_META[row.protection_status] ?? PROTECTION_META.blind_spot
                      return (
                        <tr
                          key={row.clean_num}
                          onClick={() => setSelectedNumber(row)}
                          className="cursor-pointer border-b border-border bg-card transition-colors hover:bg-secondary/50"
                        >
                          <TableTd sticky className="font-mono text-label font-semibold text-foreground">
                            {row.phone_number}
                          </TableTd>
                          <TableTd>
                            <StatusPill tone={pMeta.tone} title={pMeta.title}>
                              {pMeta.label}
                            </StatusPill>
                          </TableTd>
                          <TableTd className="font-mono text-label tabular-nums">
                            <span className="font-semibold text-foreground">{fmtNum(row.calls_received_dedup)}</span>{' '}
                            <span className="text-micro text-subtle">({fmtNum(row.calls_received_raw)} raw)</span>
                          </TableTd>
                          <TableTd className="font-mono text-label tabular-nums">
                            {row.unique_actors_called >= 2 ? (
                              <StatusPill
                                tone="warning"
                                title={`Rang ${row.unique_actors_called} distinct users/devices (${row.logged_in_victims} logged-in · ${row.logged_out_victims} logged-out)`}
                              >
                                {fmtNum(row.unique_actors_called)} users
                              </StatusPill>
                            ) : (
                              <span className="font-semibold text-foreground">{fmtNum(row.unique_actors_called)}</span>
                            )}{' '}
                            <span className="text-micro text-muted-foreground">
                              ({fmtNum(row.logged_in_victims)} in · {fmtNum(row.logged_out_victims)} out)
                            </span>
                          </TableTd>
                          <TableTd className="font-mono text-label tabular-nums">
                            <span className="text-success">{fmtNum(row.matched_calls)}</span> /{' '}
                            <span className="text-warning">{fmtNum(row.unmatched_calls)}</span>
                          </TableTd>
                          <TableTd className="font-mono text-label tabular-nums">
                            {row.user_reports_count > 0 ? (
                              <span>
                                <strong className="text-warning">{fmtNum(row.user_rejects_count)}</strong> rejected ·{' '}
                                {fmtNum(row.user_reports_count)} total
                              </span>
                            ) : (
                              <span className="text-subtle">0</span>
                            )}
                          </TableTd>
                          <TableTd className="font-mono text-label tabular-nums">
                            <span className={row.scrapes_31d_block_count >= 3 ? 'font-semibold text-destructive' : ''}>
                              {fmtNum(row.scrapes_31d_block_count)} (31d)
                            </span>{' '}
                            <span className="text-micro text-subtle">/ {fmtNum(row.scrapes_count)} all</span>
                          </TableTd>
                          <TableTd className="text-small">
                            {row.call_type_name ? (
                              <Tag mono={false}>{row.call_type_name}</Tag>
                            ) : (
                              <span className="font-mono text-micro text-subtle">—</span>
                            )}
                          </TableTd>
                          <TableTd className="text-small">
                            {row.platform_name ? (
                              <span className="font-medium text-foreground">{row.platform_name}</span>
                            ) : (
                              <span className="font-mono text-micro text-subtle">—</span>
                            )}
                          </TableTd>
                          <TableTd
                            className="font-mono text-micro text-muted-foreground"
                            title={fmtShortDateTime(row.last_ring)}
                          >
                            {fmtRelTime(row.last_ring)}
                          </TableTd>
                        </tr>
                      )
                    })}
                  </ResizableTable>
                </div>
              )}

              {/* TAB 2: Active App Users & Browsers Directory */}
              {directoryTab === 'actors' && (
                <div>
                  <ResizableTable
                    columns={actorColumns}
                    storageKey="calls-godview-actors-table"
                    theme={theme}
                    isEmpty={filteredActors.length === 0}
                    emptyMessage="No active users or devices match the current filters."
                    onRefresh={() => load(true)}
                    refreshing={refreshing}
                    lastUpdated={lastUpdated}
                    toolbar={
                      <Toolbar>
                        <SearchInput
                          value={actorSearch}
                          onChange={setActorSearch}
                          placeholder="Search email, user, device, phone model, city…"
                        />
                        <span className="font-mono text-micro text-muted-foreground">
                          {filteredActors.length} of {data.top_active_actors.length} users &amp; devices
                        </span>
                      </Toolbar>
                    }
                  >
                    {filteredActors.map((actor) => (
                      <tr
                        key={actor.actor_id}
                        onClick={() => setSelectedActor(actor)}
                        className="cursor-pointer border-b border-border bg-card transition-colors hover:bg-secondary/50"
                      >
                        <TableTd sticky className="font-mono text-label">
                          {actor.is_logged_in ? (
                            <div className="truncate">
                              <span className="font-semibold text-signal">
                                {actor.user_email_masked ?? actor.user_id}
                              </span>
                              <span className="block truncate text-micro text-subtle">{actor.user_id}</span>
                            </div>
                          ) : (
                            <div className="truncate">
                              <span className="text-foreground">{actor.primary_browser_id ?? actor.actor_id}</span>
                              <span className="block text-micro text-subtle">logged-out browser (web / app)</span>
                            </div>
                          )}
                        </TableTd>
                        <TableTd>
                          {actor.is_logged_in ? (
                            <StatusPill tone="signal">Logged in</StatusPill>
                          ) : (
                            <StatusPill tone="neutral">Logged out</StatusPill>
                          )}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          {fmtNum(actor.linked_browsers_count)}
                        </TableTd>
                        <TableTd className="font-mono text-micro">
                          {actor.device_model ? (
                            <span className="text-foreground">{actor.device_model}</span>
                          ) : (
                            <span className="text-subtle">—</span>
                          )}
                        </TableTd>
                        <TableTd className="font-mono text-micro">
                          {actor.city || actor.country ? (
                            <span>
                              {actor.city ?? '—'} {actor.country ? `(${actor.country})` : ''}
                            </span>
                          ) : (
                            <span className="text-subtle">—</span>
                          )}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          {actor.calls_received_dedup > 0 ? (
                            <span>
                              <strong className="text-signal">{fmtNum(actor.calls_received_dedup)}</strong>{' '}
                              <span className="text-micro text-success">({fmtNum(actor.calls_matched)} matched)</span>
                            </span>
                          ) : (
                            <span className="text-subtle">0</span>
                          )}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          {actor.call_submissions > 0 ? (
                            <span className="font-semibold text-warning">{fmtNum(actor.call_submissions)}</span>
                          ) : (
                            <span className="text-subtle">0</span>
                          )}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          {fmtNum(actor.manual_searches)}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">{fmtNum(actor.votes_cast)}</TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          <Tag>{actor.active_days}d</Tag>
                        </TableTd>
                        <TableTd
                          className="font-mono text-micro text-muted-foreground"
                          title={fmtShortDateTime(actor.last_seen)}
                        >
                          {fmtRelTime(actor.last_seen)}
                        </TableTd>
                      </tr>
                    ))}
                  </ResizableTable>
                </div>
              )}

              {/* TAB 3: Dataset Blind Spots */}
              {directoryTab === 'blindspots' && (
                <div>
                  <ResizableTable
                    columns={blindSpotColumns}
                    storageKey="calls-godview-blindspots-table"
                    theme={theme}
                    isEmpty={filteredBlindSpots.length === 0}
                    emptyMessage="No unmatched phone numbers found."
                    onRefresh={() => load(true)}
                    refreshing={refreshing}
                    lastUpdated={lastUpdated}
                    toolbar={
                      <Toolbar>
                        <SearchInput
                          value={blindSpotSearch}
                          onChange={setBlindSpotSearch}
                          placeholder="Filter phone number, channel, later classification…"
                        />
                        <span className="font-mono text-micro text-muted-foreground">
                          {filteredBlindSpots.length} of {data.blind_spot_numbers.length} unmatched numbers
                        </span>
                      </Toolbar>
                    }
                  >
                    {filteredBlindSpots.map((b) => (
                      <tr
                        key={b.clean_num}
                        className="border-b border-border bg-card transition-colors hover:bg-secondary/50"
                      >
                        <TableTd sticky className="font-mono text-label font-semibold text-foreground">
                          {b.phone_number}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          <span className={b.unique_actors >= 2 ? 'font-semibold text-destructive' : 'text-foreground'}>
                            {fmtNum(b.unique_actors)} users
                          </span>{' '}
                          <span className="text-micro text-muted-foreground">
                            ({fmtNum(b.logged_in_actors)} in · {fmtNum(b.logged_out_actors)} out)
                          </span>
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">
                          {b.mobilesearch_calls > 0 ? (
                            <span className="font-semibold text-signal">{fmtNum(b.mobilesearch_calls)}</span>
                          ) : (
                            <span className="text-subtle">0</span>
                          )}
                        </TableTd>
                        <TableTd className="font-mono text-label tabular-nums">{fmtNum(b.manual_searches)}</TableTd>
                        <TableTd className="font-mono text-label tabular-nums font-semibold">
                          {fmtNum(b.total_lookups)}
                        </TableTd>
                        <TableTd>
                          <div className="flex flex-wrap gap-1">
                            {(b.sources ?? []).map((src) => (
                              <Tag key={src}>{src}</Tag>
                            ))}
                          </div>
                        </TableTd>
                        <TableTd>
                          {b.later_user_reports > 0 ? (
                            <StatusPill tone="warning">
                              {b.later_user_reports} report{b.later_user_reports > 1 ? 's' : ''} (
                              {b.later_call_type_name ?? 'rejected on ring'})
                            </StatusPill>
                          ) : (
                            <StatusPill tone="danger">Not yet reported</StatusPill>
                          )}
                        </TableTd>
                        <TableTd
                          className="font-mono text-micro text-muted-foreground"
                          title={fmtShortDateTime(b.first_seen)}
                        >
                          {fmtRelTime(b.first_seen)}
                        </TableTd>
                        <TableTd
                          className="font-mono text-micro text-muted-foreground"
                          title={fmtShortDateTime(b.last_seen)}
                        >
                          {fmtRelTime(b.last_seen)}
                        </TableTd>
                      </tr>
                    ))}
                  </ResizableTable>
                </div>
              )}
            </Module>
          </ModuleStack>
        </div>
      )}

      {/* ── Drill-down Dialog: Incoming Phone Number ── */}
      <Dialog open={!!selectedNumber} onOpenChange={(open) => !open && setSelectedNumber(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono text-h2">
              {selectedNumber && (
                <PrivateItem id={`num:${selectedNumber.clean_num}`} privacy={dialogPrivacy}>
                  <span>{selectedNumber.phone_number}</span>
                </PrivateItem>
              )}
              <PrivateListToggle privacy={dialogPrivacy} />
            </DialogTitle>
            <DialogDescription>
              Incoming call details and 31-day auto-block standing for{' '}
              {selectedNumber && (
                <PrivateItem id={`num:${selectedNumber.clean_num}`} privacy={dialogPrivacy}>
                  <code className="font-mono">{selectedNumber.clean_num}</code>
                </PrivateItem>
              )}
              .
            </DialogDescription>
          </DialogHeader>
          {selectedNumber && (
            <div className="mt-2 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone={PROTECTION_META[selectedNumber.protection_status].tone}>
                  {PROTECTION_META[selectedNumber.protection_status].label}
                </StatusPill>
                {selectedNumber.call_type_name && <Tag mono={false}>{selectedNumber.call_type_name}</Tag>}
                {selectedNumber.platform_name && (
                  <PrivateItem id={`brand:${selectedNumber.platform_name}`} privacy={dialogPrivacy}>
                    <Tag mono={false}>Brand: {selectedNumber.platform_name}</Tag>
                  </PrivateItem>
                )}
              </div>
              <Fields
                fields={[
                  {
                    label: 'Calls screened',
                    value: `${fmtNum(selectedNumber.calls_received_dedup)} calls (${fmtNum(
                      selectedNumber.calls_received_raw,
                    )} rings)`,
                  },
                  {
                    label: 'Users called',
                    value: `${fmtNum(selectedNumber.unique_actors_called)} users (${fmtNum(
                      selectedNumber.logged_in_victims,
                    )} logged in · ${fmtNum(selectedNumber.logged_out_victims)} logged out)`,
                  },
                  {
                    label: 'Call dataset match',
                    value: `${fmtNum(selectedNumber.matched_calls)} matched · ${fmtNum(
                      selectedNumber.unmatched_calls,
                    )} unmatched`,
                  },
                  {
                    label: 'Call reports from consumers',
                    value: `${fmtNum(selectedNumber.user_rejects_count)} rejected on ring · ${fmtNum(
                      selectedNumber.user_reports_count,
                    )} total reports`,
                  },
                  {
                    label: 'Dataset coverage',
                    value: `${fmtNum(selectedNumber.scrapes_31d_block_count)} blocking entries in last 31d / ${fmtNum(
                      selectedNumber.scrapes_count,
                    )} all-time`,
                  },
                  {
                    label: 'First / last call in window',
                    value: `${fmtShortDateTime(selectedNumber.first_ring)} → ${fmtShortDateTime(
                      selectedNumber.last_ring,
                    )}`,
                  },
                ]}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Drill-down Dialog: Active Actor (User / Browser) ── */}
      <Dialog open={!!selectedActor} onOpenChange={(open) => !open && setSelectedActor(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono text-h2">
              {selectedActor && (
                <PrivateItem id={`actor:${selectedActor.actor_id}`} privacy={dialogPrivacy}>
                  <span>
                    {selectedActor.is_logged_in
                      ? selectedActor.user_email_masked ?? selectedActor.user_id
                      : selectedActor.primary_browser_id ?? selectedActor.actor_id}
                  </span>
                </PrivateItem>
              )}
              <PrivateListToggle privacy={dialogPrivacy} />
            </DialogTitle>
            <DialogDescription>
              Activity across incoming call screening, call reports, manual lookups, and community votes.
            </DialogDescription>
          </DialogHeader>
          {selectedActor && (
            <div className="mt-2 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {selectedActor.is_logged_in ? (
                  <StatusPill tone="signal">Logged-in account</StatusPill>
                ) : (
                  <StatusPill tone="neutral">Logged-out device</StatusPill>
                )}
                {selectedActor.device_model && <Tag>{selectedActor.device_model}</Tag>}
                <Tag>{selectedActor.active_days} active days</Tag>
              </div>
              <Fields
                fields={[
                  {
                    label: 'User ID',
                    value: selectedActor.user_id ? (
                      <PrivateItem id={`actor:${selectedActor.actor_id}`} privacy={dialogPrivacy}>
                        <span>{selectedActor.user_id}</span>
                      </PrivateItem>
                    ) : (
                      '— (not logged in)'
                    ),
                  },
                  {
                    label: 'Device ID',
                    value: (
                      <span>
                        <PrivateItem id={`actor:${selectedActor.actor_id}`} privacy={dialogPrivacy}>
                          <span>{selectedActor.primary_browser_id ?? '—'}</span>
                        </PrivateItem>{' '}
                        ({fmtNum(selectedActor.linked_browsers_count)} linked)
                      </span>
                    ),
                  },
                  {
                    label: 'Phone & network',
                    value: `${selectedActor.device_model ?? 'Unknown phone'} · ${selectedActor.city ?? '—'}, ${
                      selectedActor.country ?? '—'
                    } · ASN ${selectedActor.asn ?? '—'}`,
                  },
                  {
                    label: 'Calls screened',
                    value: `${fmtNum(selectedActor.calls_received_dedup)} calls (${fmtNum(
                      selectedActor.calls_received_raw,
                    )} rings · ${fmtNum(selectedActor.calls_matched)} matched)`,
                  },
                  {
                    label: 'Call reports',
                    value: fmtNum(selectedActor.call_submissions),
                  },
                  {
                    label: 'Manual lookups & votes',
                    value: `${fmtNum(selectedActor.manual_searches)} lookups · ${fmtNum(
                      selectedActor.votes_cast,
                    )} votes`,
                  },
                  {
                    label: 'First / last seen in window',
                    value: `${fmtShortDateTime(selectedActor.first_seen)} → ${fmtShortDateTime(
                      selectedActor.last_seen,
                    )}`,
                  },
                ]}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageFrame>
  )
}
